import React, { useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { NamedLink } from '../../../../components';
import { landingSectionSurfaceClassName } from '../../../LandingPage/landingSectionSurface';

import SectionContainer from '../SectionContainer';
import css from './SectionLogoSlider.module.css';

/**
 * Coworking spaces active on the platform.
 *
 * Each entry is a real provider on cowork24.ch; the `logo` is their public profile
 * image, pulled from the live Marketplace API (signed imgix URLs — see
 * `optimizeLogoUrl` for why the signature must stay intact). Keep this list in sync
 * with the marketplace and only include genuine coworking / flexible-office brands
 * (exclude the platform's own account and non-space tenants).
 *
 * Entries must be a brand MARK. 'reforum' was dropped because its profile image is a portrait
 * photograph of a person — in a row of circular logos it reads as a stray avatar and breaks the
 * wall. It is a data gap, not a code one: re-add the entry as soon as that provider supplies a
 * logo. Note also that '1905 Business Center Baden' is near-white on white and barely visible.
 */
const LOGOS = [
  {
    id: 'impact-hub-zuerich',
    name: 'Impact Hub Zürich',
    profileId: '690c4e4c-a5d3-4334-8b10-684e09754a44',
    logo:
      'https://sharetribe.imgix.net/68a32a0c-65b6-47ae-97b1-43643e03bc76/691d9c3c-0e4b-451b-a9e5-e1dd4081b66f?auto=format&crop=edges&fit=crop&h=480&w=480&s=7541ef75f5a032d6273564db2dbaed75',
  },
  {
    id: 'satellite-office-gryffenberg',
    name: 'Satellite Office GmbH - Gryffenberg',
    profileId: '6a185b63-4823-442d-8328-6bbff12f7aa3',
    logo:
      'https://sharetribe.imgix.net/68a32a0c-65b6-47ae-97b1-43643e03bc76/6a185b89-65f7-4b13-8f3f-5c3d7f633b39?auto=format&crop=edges&fit=crop&h=480&w=480&s=6e473ee4decca41faecd7bff53813335',
  },
  {
    id: 'headsquarter',
    name: 'Headsquarter',
    profileId: '68c7c453-28a4-43b1-8e2b-95f4cd3b0cd4',
    logo:
      'https://sharetribe.imgix.net/68a32a0c-65b6-47ae-97b1-43643e03bc76/6a037037-cecd-4a9f-9b6b-5180998c206d?auto=format&crop=edges&fit=crop&h=480&w=480&s=6ef65e799b209d2133d30ac596e8aa61',
  },
  {
    id: 'coworking-seefeld',
    name: 'Coworking Seefeld',
    profileId: '68de5470-361b-4e6a-b89d-abb71eb98c08',
    logo:
      'https://sharetribe.imgix.net/68a32a0c-65b6-47ae-97b1-43643e03bc76/698f3d90-c887-4a6b-9d73-bb38f62dc864?auto=format&crop=edges&fit=crop&h=480&w=480&s=a63097bc98b93eb2a0a83f1377ad4c2f',
  },
  {
    id: 'greencityoffice',
    name: 'GreencityOffice',
    profileId: '6a156dab-d2cf-4036-875b-d5a15a9d5396',
    logo:
      'https://sharetribe.imgix.net/68a32a0c-65b6-47ae-97b1-43643e03bc76/6a156dfe-22b9-434e-a2aa-ea462e530426?auto=format&crop=edges&fit=crop&h=480&w=480&s=571229144a9fc79c4c63e5a828ce1f22',
  },
  {
    id: 'flexoffice-schweiz',
    name: 'FlexOffice (Schweiz) AG',
    profileId: '68e36665-2127-41f3-8d12-e070ca3ff11d',
    logo:
      'https://sharetribe.imgix.net/68a32a0c-65b6-47ae-97b1-43643e03bc76/690367ae-f58e-43b0-b823-bea8bad6559e?auto=format&crop=edges&fit=crop&h=480&w=480&s=43282722dc8fe05b5b40d61bd3a61b50',
  },
  {
    id: 'story-thusis',
    name: 'STORY Thusis',
    profileId: '69c632da-4ec1-447d-a343-15ee06c08acd',
    logo:
      'https://sharetribe.imgix.net/68a32a0c-65b6-47ae-97b1-43643e03bc76/69c7f14e-0cfa-41cf-9ada-02523f9ccfef?auto=format&crop=edges&fit=crop&h=480&w=480&s=967605fb3135f511052e7d93f57f9644',
  },
  {
    id: 'commune88',
    name: 'Commune88 CoLiving&CoWorking',
    profileId: '68fe0b26-4608-4a49-8a6a-a8cec2a12350',
    logo:
      'https://sharetribe.imgix.net/68a32a0c-65b6-47ae-97b1-43643e03bc76/68fe0d31-15bd-4c39-9e3b-e1ac148b14a8?auto=format&crop=edges&fit=crop&h=480&w=480&s=107656f1576a8f748bd3630083d2247d',
  },
  {
    id: 'coworking-bodensee',
    name: 'Coworking Bodensee',
    profileId: '68ff49b3-29a0-4738-a68d-c883b32ece30',
    logo:
      'https://sharetribe.imgix.net/68a32a0c-65b6-47ae-97b1-43643e03bc76/68ff4fb2-1062-4637-b383-1499b28a4f79?auto=format&crop=edges&fit=crop&h=480&w=480&s=7de146605fc9a5e0124d1b3711e396fa',
  },
  {
    id: 'fabrik11',
    name: 'FABRIK11',
    profileId: '69e91588-ebd7-4607-8cf8-da78a1d6ad3d',
    logo:
      'https://sharetribe.imgix.net/68a32a0c-65b6-47ae-97b1-43643e03bc76/69e915a8-935d-4ad2-9b3b-5a736dddb2fb?auto=format&crop=edges&fit=crop&h=480&w=480&s=ee1dcd5ff8bde4be26f52edff74f8bfc',
  },
  {
    id: '1905-business-center-baden',
    name: '1905 Business Center Baden',
    profileId: '68e77aaf-d373-48da-9fc3-18eda2b83d87',
    logo:
      'https://sharetribe.imgix.net/68a32a0c-65b6-47ae-97b1-43643e03bc76/6a0370af-b39c-469f-9b47-c9e41a0200a2?auto=format&crop=edges&fit=crop&h=480&w=480&s=762962e614b49b1a30596dd36bf3125c',
  },
  {
    id: 'zeitplatz',
    name: 'ZEITPLATZ',
    profileId: '68e54aa1-5030-4a62-a647-6d3f2b1a85e5',
    logo:
      'https://sharetribe.imgix.net/68a32a0c-65b6-47ae-97b1-43643e03bc76/68e54ae5-153b-43bd-a8eb-e61d6814d6e5?auto=format&crop=edges&fit=crop&h=480&w=480&s=efd1c55082525ec1d264b35a3a020446',
  },
  {
    id: 'byro-aarau',
    name: 'BYRO Aarau',
    profileId: '6a0df675-c936-4d79-9a03-5d6da8a0fcaa',
    logo:
      'https://sharetribe.imgix.net/68a32a0c-65b6-47ae-97b1-43643e03bc76/6a0df67e-c40b-4658-aa58-32ceda7a1bef?auto=format&crop=edges&fit=crop&h=480&w=480&s=47403759cf9f4bebe79e5ed9b97d8d08',
  },
  {
    id: 'bureau-d',
    name: 'BUREAU.D',
    profileId: '6a020216-93f0-4d86-a869-5289e66246e2',
    logo:
      'https://sharetribe.imgix.net/68a32a0c-65b6-47ae-97b1-43643e03bc76/6a020482-dae6-497a-b537-ba729b36d9e5?auto=format&crop=edges&fit=crop&h=480&w=480&s=8f9677e97d308ef617a07c1ae0d4a02d',
  },
  {
    id: 'nowo-coworking',
    name: 'Nowo Coworking Space',
    profileId: '68fccdec-85ab-42b8-8a8e-d6aaaa9afd1f',
    logo:
      'https://sharetribe.imgix.net/68a32a0c-65b6-47ae-97b1-43643e03bc76/68fcd21c-ab81-4342-afeb-6b169bd0177a?auto=format&crop=edges&fit=crop&h=480&w=480&s=311fd441085e9a504fa45e8b6dd6936b',
  },
  {
    id: 'workspace4you',
    name: 'workspace4you',
    profileId: '690f5888-0006-48e6-b6a9-ef75c1639fe5',
    logo:
      'https://sharetribe.imgix.net/68a32a0c-65b6-47ae-97b1-43643e03bc76/69abeec0-25a0-47c8-ad48-d5046f092c39?auto=format&crop=edges&fit=crop&h=480&w=480&s=a84325e1fb68cb7a64b43fcb0c3a4ab4',
  },
];

const optimizeLogoUrl = url => {
  if (!url) {
    return url;
  }

  if (url.includes('sharetribe.imgix.net')) {
    // Sharetribe/imgix URLs are often signed (`s=`). Appending params invalidates the
    // signature and breaks loading — use the URL as provided.
    if (/[?&]s=/.test(url)) {
      return url;
    }
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}auto=format,compress&fit=crop&w=160&h=80`;
  }

  if (url.includes('onecdn.io/media/')) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}width=160&height=80`;
  }

  if (url.includes('media.licdn.com/dms/image/')) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}w=160&h=80`;
  }

  return url;
};

/**
 * Single logo item rendered inside the marquee track.
 * Shows a rounded avatar when a `logo` URL is provided, otherwise a styled placeholder.
 *
 * When `profileId` is set and `linkable` is true, the item links to the provider's public
 * profile (ProfilePage, `/u/:id`). The duplicated (aria-hidden) marquee set passes
 * `linkable={false}` so it stays out of the tab order and doesn't double the links.
 */
const LogoItem = ({ name, logo, color, profileId, linkable = true }) => {
  const [imageLoadFailed, setImageLoadFailed] = useState(false);

  const content =
    logo && !imageLoadFailed ? (
      <img
        src={optimizeLogoUrl(logo)}
        alt={name}
        className={css.logoImage}
        loading="eager"
        decoding="async"
        width="64"
        height="64"
        onError={() => setImageLoadFailed(true)}
      />
    ) : (
      <>
        <div className={css.logoPlaceholder} style={{ backgroundColor: color }}>
          <span className={css.logoInitials}>
            {name
              .split(' ')
              .map(w => w[0])
              .join('')
              .slice(0, 2)}
          </span>
        </div>
        <span className={css.logoName}>{name}</span>
      </>
    );

  if (profileId && linkable) {
    return (
      <NamedLink
        name="ProfilePage"
        params={{ id: profileId }}
        className={css.logoItem}
        title={name}
        ariaLabel={name}
      >
        {content}
      </NamedLink>
    );
  }

  return <div className={css.logoItem}>{content}</div>;
};

/**
 * Infinite marquee logo slider showcasing coworking spaces active on the platform.
 * Uses pure CSS animation with duplicated content for a seamless loop.
 *
 * @component
 * @param {Object} props
 * @param {string} props.sectionId - Unique section identifier
 * @param {string?} props.className - Additional CSS class
 * @param {string?} props.rootClassName - Override root CSS class
 * @param {Object?} props.defaultClasses - Shared styling classes from SectionBuilder
 * @param {Object?} props.appearance - Appearance settings
 * @param {Object?} props.options - Extra options
 * @param {string?} props.eyebrowId - Optional i18n id for a small eyebrow above the heading
 * @param {string?} props.titleId - Optional i18n id for a social-proof heading above the marquee
 * @param {string?} props.subtitleId - Optional i18n id for supporting copy under the heading
 * @returns {JSX.Element}
 */
const SectionLogoSlider = props => {
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    appearance,
    options,
    landingSurface,
    eyebrowId,
    titleId,
    subtitleId,
  } = props;

  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={classNames(rootClassName, landingSectionSurfaceClassName(landingSurface))}
      appearance={appearance}
      options={options}
    >
      <div className={css.wrapper}>
        {titleId ? (
          <div className={css.heading}>
            {eyebrowId ? (
              <span className={css.eyebrow}>
                <FormattedMessage id={eyebrowId} />
              </span>
            ) : null}
            <h2 className={css.title}>
              <FormattedMessage id={titleId} />
            </h2>
            {subtitleId ? (
              <p className={css.subtitle}>
                <FormattedMessage id={subtitleId} />
              </p>
            ) : null}
          </div>
        ) : null}
        <div className={css.marqueeContainer}>
          {/* Fade edges */}
          <div className={css.fadeLeft} />
          <div className={css.fadeRight} />

          {/* Marquee track: two identical sets for seamless loop */}
          <div className={css.marqueeTrack}>
            <div className={css.marqueeContent} aria-label="Logo slider">
              {LOGOS.map(l => (
                <LogoItem key={l.id} {...l} />
              ))}
            </div>
            <div className={css.marqueeContent} aria-hidden="true">
              {LOGOS.map(l => (
                <LogoItem key={`dup-${l.id}`} {...l} linkable={false} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </SectionContainer>
  );
};

export default SectionLogoSlider;
